import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Kbd,
} from '@shining-technologies/ui';
import { useUiStore } from '@/stores/ui';

const SHORTCUTS: { keys: string; desc: string }[] = [
  { keys: '⌘ K', desc: 'Open command palette' },
  { keys: '?', desc: 'Show this help' },
  { keys: 'g then d', desc: 'Go to dashboard' },
  { keys: 'Esc', desc: 'Close dialog / drawer' },
  { keys: '/', desc: 'Focus search in a list' },
];

/** How long after `g` a following `d` still counts as the `g d` chord. */
const CHORD_WINDOW_MS = 1000;

/**
 * An open modal surface: a Radix dialog (`data-state="open"`, incl. the kit's
 * dialogs and command menu) or the kit sidebar drawer, which is a
 * `role="dialog"` panel marked `data-open` while it is open on a phone.
 */
const OPEN_MODAL = '[role="dialog"][data-state="open"],[role="dialog"][data-open]';

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

/**
 * FIX(admin-shortcuts-in-menus): Radix Select/DropdownMenu/Combobox own their
 * own keyboard handling (typeahead, arrow nav, Escape) — the global shortcuts
 * must not compete with it. Before: `/` and `g d` fired even while focus was
 * inside an open Select/DropdownMenu/Combobox, stealing keystrokes meant for
 * that menu's typeahead. After: ignored whenever the event target is inside a
 * listbox/menu/option/menuitem or is itself a combobox trigger.
 */
function isInsideMenu(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest('[role="listbox"],[role="menu"],[role="option"],[role="menuitem"]') ||
      target.closest('[role="combobox"]'),
  );
}

/**
 * Global single-key shortcuts + the `?` help overlay.
 *
 * FIX(admin-shortcuts): Before: the overlay advertised `g then d` and `/`, but
 * only `?` (and ⌘K, in CommandPalette) were wired — both did nothing. After:
 * `g` then `d` (within 1s) goes to /dashboard; `/` focuses the page's list
 * search box (`SearchInput`, tagged `data-page-search`) or, on a page without
 * one, opens the command palette. All are ignored while typing in a field or
 * with a modifier held; `/` and `g d` also while a dialog (or the mobile nav
 * drawer) is open, or while focus is inside a Radix menu/listbox/combobox (see
 * `isInsideMenu`).
 */
export function ShortcutsOverlay() {
  const open = useUiStore((s) => s.shortcutsOpen);
  const setOpen = useUiStore((s) => s.setShortcutsOpen);
  const navigate = useNavigate();
  const lastG = useRef(0);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      if (isInsideMenu(e.target)) return;
      if (e.key === '?') {
        e.preventDefault();
        setOpen(true);
        return;
      }
      // Page-level shortcuts stay out of the way while a modal owns focus.
      if (document.querySelector(OPEN_MODAL)) return;
      if (e.key === '/') {
        e.preventDefault();
        const search = document.querySelector<HTMLInputElement>('input[data-page-search]');
        if (search) search.focus();
        else useUiStore.getState().setCommandPaletteOpen(true);
        return;
      }
      if (e.key === 'g') {
        lastG.current = Date.now();
        return;
      }
      if (e.key === 'd' && lastG.current && Date.now() - lastG.current <= CHORD_WINDOW_MS) {
        lastG.current = 0;
        e.preventDefault();
        navigate('/dashboard');
        return;
      }
      lastG.current = 0;
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [setOpen, navigate]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription className="sui-visually-hidden">
            Keyboard shortcuts available across the admin.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <ul className="flex flex-col gap-2">
            {SHORTCUTS.map((s) => (
              <li key={s.keys} className="flex items-center justify-between gap-4 text-sm">
                <span className="text-muted-foreground">{s.desc}</span>
                <Kbd>{s.keys}</Kbd>
              </li>
            ))}
          </ul>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
